import { Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Typography } from "antd";
import { Dropdown, OptionMaster, Tank } from "src/entity/tank/tank.entity";
import { AreaProps } from "src/entity/company/company-response.entity";
import { SettingEmissionControlTank, SettingTankType } from "src/entity/settings/tank";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { SetStateAction, useEffect, useState } from "react";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { generateErrorMessage } from "src/utils/error-message";
import CustomFormDatePicker from "src/components/AntdCustomPart/CustomFormDatePicker/CustomFormDatePicker";
import WrapCenter from "src/components/WrapCenter/WrapCenter";
import { PlusCircleOutlined } from "@ant-design/icons";
import { BsTrashFill } from "react-icons/bs";
import { componentPalette } from "src/theme/component-palette";

const { Text } = Typography;

const SetupTankModal = (props: {
  open: boolean;
  id: string;
  mode: "insert" | "update";
  initData: Tank;
  loading: boolean;
  companyId: string;
  tankTypeMaster: SettingTankType[];
  tankEmissionControlMaster: SettingEmissionControlTank[];
  emissionMaster: OptionMaster[];
  areaMaster: AreaProps[];
  isDuplicate: boolean;
  onDuplicateChange: (isDup: boolean) => void;
  onCloseModal: () => void;
  onSave: (data: Tank, id: string) => void;
}) => {
  const {
    open,
    companyId,
    tankEmissionControlMaster,
    areaMaster,
    initData,
    id,
    isDuplicate,
    loading,
    tankTypeMaster,
    emissionMaster,
    onSave,
    onDuplicateChange,
    onCloseModal,
  } = props;
  const [data, setData] = useState<Tank>(initData);
  const [optionTankType, setOptionTankType] = useState<Dropdown[]>([]);
  const [optionEmissionControlTank, setOptionEmissionControlTank] = useState<Dropdown[]>([]);
  const [optionEmission, setOptionEmission] = useState<Dropdown[]>([]);
  const [optionArea, setOptionArea] = useState<Dropdown[]>([]);
  const [errorDuplicate, setErrorDuplicate] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [selectedEmissions, setSelectedEmissions] = useState<string[]>([]);

  const initMasterData = () => {
    const tmpOptTankType: Dropdown[] = tankTypeMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.tankType,
      };
    });
    const tmpOptEmissionComtrol: Dropdown[] = tankEmissionControlMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.emissionControl,
      };
    });
    const tmpOptEff: Dropdown[] = tankEmissionControlMaster.map((item, index) => {
      return {
        key: String(index),
        value: item.efficiency ?? 0,
        label: String(item.efficiency),
      };
    });
    const tmpArea: Dropdown[] = areaMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.areaName,
      };
    });

    const tmpOptEmission: Dropdown[] = emissionMaster.map((item, index) => {
      return {
        key: String(index),
        value: item.value,
        label: item.label,
      };
    });
    setOptionTankType(tmpOptTankType);
    setOptionArea(tmpArea);
    setOptionEmissionControlTank(tmpOptEmissionComtrol);
    setOptionEmission(tmpOptEmission);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const formValue = form.getFieldsValue();
      onSave(formValue, id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    // Validate Portion
    const emissionControlDetails = allValues.emissionControlDetails || [];
    const totalPortion = emissionControlDetails?.reduce((sum: number, item: any) => sum + (item?.portion || 0), 0);
    form.setFields(
      emissionControlDetails?.map((item: any, index: number) => ({
        name: ["emissionControlDetails", index, "portion"],
        errors: [],
      })),
    );
    if (totalPortion > 100) {
      emissionControlDetails.forEach((item: any, index: number) => {
        form.setFields([
          {
            name: ["emissionControlDetails", index, "portion"],
            errors: ["The sum of all portions must be less than 100%."],
          },
        ]);
      });
    }

    // Validate Duplicate
    if (["tankId", "area", "chemicalName", "tankTypeId"].some((key) => changedValues[key])) {
      onDuplicateChange(false);
    }

    // Validate Control exception date
    if (changedValues.arrControlExceptionDate) {
      const newExceptions: string[] = Array.from(new Set<string>(changedValues.arrControlExceptionDate));
      form.setFieldsValue({
        arrControlExceptionDate: newExceptions,
      });
    }
  };

  const handleSelectChangeEmissionDeatils = (index: number, value: string | number) => {
    const efficiency = tankEmissionControlMaster.find((item) => item._id === value)?.efficiency;
    form.setFieldsValue({
      emissionControlDetails: form.getFieldValue("emissionControlDetails").map((field: any, idx: number) => {
        if (idx === index) {
          return {
            ...field,
            efficiency: efficiency, // Update efficiency
          };
        }
        return field;
      }),
    });
  };

  const handleSelectChange = (key: keyof Tank, value: string | number) => {
    if (data) {
      setData({
        ...data,
        [key]: value ?? "",
      });
    }
  };

  useEffect(() => {
    if (!data?.emissionControlId) {
      data.efficiency = 0;
    }
    form.setFieldsValue({ ...data });
    const emission = data.emission;
    const checkEmission: SetStateAction<string[]> = [];
    if (emission) {
      Object.keys(emission).forEach((key) => {
        const emissionKey = key as keyof typeof emission;
        if (emission[emissionKey] === true) {
          checkEmission.push(emissionKey);
        }
      });
      setSelectedEmissions(checkEmission);
    }
    initMasterData();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      checkboxEmission: selectedEmissions,
    });
  }, [selectedEmissions]);

  useEffect(() => {
    const errorText = isDuplicate ? "Tank ID, Area, Chemical Name and Tank Type already exist in the system." : "";
    setErrorDuplicate(errorText);
    form.setFields(
      ["tankId", "area", "chemicalName", "tankTypeId"].map((field) => ({
        name: field,
        errors: isDuplicate ? [""] : [],
      })),
    );
  }, [isDuplicate]);

  return (
    <div>
      <Modal
        centered
        maskClosable={false}
        title={<ModalTitle title="Tank Information" />}
        open={open}
        onCancel={onCloseModal}
        width={900}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" onClick={handleSave} loading={loading}>
              Save
            </Button>
          </Row>
        }
      >
        {errorDuplicate && (
          <>
            <Row className="w-full" justify={"center"}>
              <Text type="danger">{errorDuplicate}</Text>
            </Row>
          </>
        )}

        <Form
          size="middle"
          layout="vertical"
          autoComplete="off"
          form={form}
          requiredMark={false}
          onValuesChange={handleValuesChange}
          initialValues={{
            checkboxEmission: selectedEmissions,
            companyId: companyId,
            year: data.year,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Tank ID"} required />}
                name="tankId"
                rules={[{ required: true, message: generateErrorMessage("Tank ID") }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Area"} required />}
                name="area"
                rules={[{ required: true, message: generateErrorMessage("Area") }]}
              >
                <Select options={optionArea} onChange={(value) => handleSelectChange("area", value)} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Chemical Name"} required />}
                name="chemicalName"
                rules={[{ required: true, message: generateErrorMessage("Chemical Name") }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Tank Type"} required />}
                name="tankTypeId"
                rules={[{ required: true, message: generateErrorMessage("Tank Type") }]}
              >
                <Select options={optionTankType} onChange={(value) => handleSelectChange("tankTypeId", value)} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
              <CustomFormDatePicker
                name={"arrControlExceptionDate"}
                label={"Control Exception Date"}
                customClassName={"exception-date-period"}
                mode={"date"}
              />
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Emission"} required />}
                name="checkboxEmission"
                rules={[{ required: true, message: "Please select at least one emission" }]}
              >
                <Checkbox.Group style={{ width: "100%" }}>
                  {optionEmission.map((option) => (
                    <Col span={4} key={option.value}>
                      <Checkbox value={option.value}>{option.label}</Checkbox>
                    </Col>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </Col>

            <Col span={24} style={{ paddingTop: "20px" }}>
              <Form.List name="emissionControlDetails">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Row gutter={[16, 0]} key={key} style={{ display: "flex", justifyContent: "center" }}>
                        <Col span={8}>
                          <Form.Item
                            style={{ marginBottom: "0px" }}
                            name={[name, "emissionControlId"]}
                            label={<FormLabel label={"Emission Control"} />}
                          >
                            <Select
                              allowClear
                              options={optionEmissionControlTank}
                              onChange={(value) => {
                                handleSelectChangeEmissionDeatils(index, value);

                                // If the clear button is clicked (value is undefined), set efficiency is 0
                                if (value === undefined) {
                                  form.setFieldsValue({
                                    emissionControlDetails: {
                                      [name]: {
                                        // portion: null,
                                        efficiency: 0,
                                      },
                                    },
                                  });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <Form.Item style={{ marginTop: "0px" }} name={[name, "efficiency"]} label={<FormLabel label={"Efficiency"} />}>
                            <InputNumber style={{ width: "100%" }} disabled={true} addonAfter={"%"} defaultValue={0} precision={2} />
                          </Form.Item>
                        </Col>

                        <Col
                          span={1}
                          style={{
                            textAlign: "right",
                            paddingTop: "1px",
                          }}
                        >
                          <BsTrashFill
                            size={16}
                            color={componentPalette.blueGrey}
                            style={{
                              cursor: "pointer",
                              marginTop: "40px",
                            }}
                            onClick={() => {
                              if (name === 0) {
                                const fieldsToReset = ["portion", "emissionControlId", "efficiency"].map((field) => ({
                                  name: ["emissionControlDetails", name, field],
                                  value: null,
                                }));
                                form.setFields(fieldsToReset);
                              } else {
                                form.setFieldsValue({ emissionControlDetails: {} }); // ลบค่าทั้งหมดในกลุ่ม
                                remove(name);
                              }
                            }}
                          />
                        </Col>
                      </Row>
                    ))}

                    <Form.Item>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          type={"primary"}
                          onClick={() => {
                            add();
                          }}
                          disabled={(form.getFieldValue("emissionControlDetails") || []).length >= 2}
                        >
                          <WrapCenter>
                            <PlusCircleOutlined />
                          </WrapCenter>
                        </Button>
                      </div>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SetupTankModal;
